/**
 * db.ts — the shape of every stored row (P2/US-202).
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * WHY ROW SHAPES LIVE IN THE CONTRACT AND NOT IN `apps/api`
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * Same reason as every other shape in this package: if it is defined twice, that is the bug
 * (metric M9). These rows are read by the API today and by the nightly export (US-211), the restore
 * procedure, and whatever tooling ever has to answer an audit in 2036. One definition.
 *
 * They are also the boundary where **`unknown` from the database becomes typed data**. D1 returns
 * `Record<string, unknown>`; a cast would make that a lie the compiler believes. Parsing makes it a
 * fact, and a row that has drifted from the schema fails at the read rather than three frames later
 * in a renderer.
 *
 * RULES THIS FILE OBEYS — the package's existing ones, unchanged
 *  - No formulas, and no import of `@ronceray/engine`. `test/optimise.test.ts`'s import scan reads
 *    every file in `src/`, so this one is covered the moment it exists.
 *  - Every field carries a `.describe()`.
 *
 * @see DATA.md — the tenancy rule, the integrity-hash chain, and the seam with better-auth.
 */

import { z } from 'zod';

import { savedModuleSchema } from './saved-file';

/* ================================================================================================
 * Primitives
 * ============================================================================================== */

/**
 * A row id. `nanoid` per ARCHITECTURE §5, but the schema only demands a non-empty string: pinning
 * the generator's alphabet here would make swapping it a schema change for no gain.
 */
export const rowIdSchema = z.string().min(1).describe('Row identifier (nanoid).');

/**
 * A SQLite `datetime('now')` timestamp: `YYYY-MM-DD HH:MM:SS`, UTC.
 *
 * Deliberately NOT `z.iso.datetime()`. SQLite's default has a space where ISO-8601 has a `T` and no
 * timezone designator, and validating against a format the database does not produce would reject
 * every row it wrote. Converting to ISO is a presentation concern.
 */
export const sqlTimestampSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)
  .describe("SQLite UTC timestamp from datetime('now'), e.g. `2026-09-05 14:32:10`.");

/** A SHA-256 digest as lowercase hex. */
export const sha256HexSchema = z
  .string()
  .regex(/^[0-9a-f]{64}$/)
  .describe('SHA-256 digest, lowercase hex.');

/** Roles, matching the CHECK constraint the auth table will carry. */
export const USER_ROLES = ['admin', 'member'] as const;
export const userRoleSchema = z
  .enum(USER_ROLES)
  .describe('Membership role. Only `admin` may invite (D5).');
export type UserRole = z.infer<typeof userRoleSchema>;

/* ================================================================================================
 * organisations
 * ============================================================================================== */

export const organisationRowSchema = z
  .object({
    id: rowIdSchema,
    name: z.string().min(1).describe('Display name of the client firm.'),
    created_at: sqlTimestampSchema,
  })
  .describe('One tenant. Every other row in the application schema hangs off one of these.');

export type OrganisationRow = z.infer<typeof organisationRowSchema>;

/* ================================================================================================
 * projects
 * ============================================================================================== */

export const projectRowSchema = z
  .object({
    id: rowIdSchema,
    org_id: rowIdSchema.describe('Owning organisation. Every query filters on it.'),
    name: z.string().min(1).describe('Project name, e.g. "Halle logistique Saint-Denis".'),
    address: z.string().nullable().describe('Free text, printed on the note de calcul.'),
    created_by: rowIdSchema.describe(
      "better-auth user id. Not a foreign key yet — see DATA.md, 'The seam with better-auth'.",
    ),
    created_at: sqlTimestampSchema,
    updated_at: sqlTimestampSchema,
  })
  .describe('A client project. Cases hang off it.');

export type ProjectRow = z.infer<typeof projectRowSchema>;

/* ================================================================================================
 * cases
 * ============================================================================================== */

export const caseRowSchema = z
  .object({
    id: rowIdSchema,
    org_id: rowIdSchema.describe(
      'Owning organisation. DENORMALISED from `projects` so every tenancy filter hits this row ' +
        'directly rather than joining (ARCHITECTURE §5).',
    ),
    project_id: rowIdSchema,
    module: savedModuleSchema.describe('Which calculator produced this: lamine, creux or bois.'),
    title: z.string().nullable().describe('User-supplied label, e.g. "Poutre de plancher N3".'),
    schema_version: z
      .int()
      .positive()
      .describe('Saved-file schema version the inputs conform to. See `./saved-file`.'),
    inputs_json: z.string().min(1).describe('Canonical JSON of the request inputs.'),
    results_json: z
      .string()
      .min(1)
      .describe(
        'Canonical JSON of the frozen results snapshot (D10). ALWAYS server-computed: a result ' +
          'the client supplied is one nobody can vouch for.',
      ),
    engine_version: z.string().min(1).describe('Engine that produced the snapshot (D7).'),
    catalog_version: z.string().min(1).describe('Catalog edition used (D7).'),
    integrity_hash: sha256HexSchema.describe(
      'sha256(canonical(inputs) ‖ canonical(results) ‖ engine_version ‖ catalog_version). ' +
        'Recomputable from the stored fields — that recomputation IS the audit procedure.',
    ),
    created_by: rowIdSchema,
    created_at: sqlTimestampSchema,
    updated_at: sqlTimestampSchema,
    deleted_at: sqlTimestampSchema
      .nullable()
      .describe('Soft delete only (D11). There is no hard-delete path in the product.'),
  })
  .describe('A saved calculation. The engineering record the 10-year retention policy is about.');

export type CaseRow = z.infer<typeof caseRowSchema>;

/* ================================================================================================
 * case_revisions
 * ============================================================================================== */

export const caseRevisionRowSchema = z
  .object({
    id: rowIdSchema,
    case_id: rowIdSchema,
    org_id: rowIdSchema,
    results_json: z.string().min(1),
    engine_version: z.string().min(1),
    catalog_version: z.string().min(1),
    integrity_hash: sha256HexSchema.describe(
      'The hash this snapshot had while it was current. Carried across unchanged, never ' +
        'recomputed: a revision that fails its own check must stay visibly broken.',
    ),
    superseded_at: sqlTimestampSchema,
  })
  .describe(
    'What a case said before a recalculation (D10). Immutable and never deleted — a note may ' +
      'already have been issued from it.',
  );

export type CaseRevisionRow = z.infer<typeof caseRevisionRowSchema>;

/* ================================================================================================
 * usage_log
 * ============================================================================================== */

export const usageLogRowSchema = z
  .object({
    id: z.int(),
    org_id: rowIdSchema,
    user_id: rowIdSchema,
    endpoint: z.string().min(1).describe('e.g. `/v1/calc/lamine`.'),
    module: savedModuleSchema.nullable(),
    input_hash: sha256HexSchema.describe(
      'THE ONLY THING RECORDED ABOUT THE REQUEST BODY (D9 as amended). Clients’ load cases are ' +
        'commercially sensitive to them; a hash is enough to spot systematic harvesting. There is ' +
        'no column in this table that could hold an input value.',
    ),
    cost_units: z
      .int()
      .nonnegative()
      .describe('calc = 1; optimise per OPTIMISE.md; cache hit = 0.'),
    cached: z.int().describe('1 when served from the response cache. Logged, but charged nothing.'),
    ts: sqlTimestampSchema,
  })
  .describe('One metered request. Hashes and metadata only, never payloads.');

export type UsageLogRow = z.infer<typeof usageLogRowSchema>;

/* ================================================================================================
 * The tenant-owned tables
 * ============================================================================================== */

/**
 * Tables that carry an `org_id` and must never be queried without one.
 *
 * Exported as data rather than prose so the guard in `apps/api/test/db.test.ts` can iterate it: a
 * table added here without a matching org-scoped accessor fails that test, which is the point.
 */
export const TENANT_OWNED_TABLES = [
  'projects',
  'cases',
  'case_revisions',
  'usage_log',
  'invites',
] as const;

export type TenantOwnedTable = (typeof TENANT_OWNED_TABLES)[number];
